import React, { useEffect, useState, useCallback } from "react";
import { Card, Button } from "react-bootstrap";
import { TransportSearchParams, TransportItem, Message, SearchResponse } from "../../types";
import {
  getTopOffers,
  formatDate,
  formatTime,
  formatPrice,
} from "../../functions/Utils";
import api from "../../api";
import { apiRoutes } from "../../routes/routes";
import OfferCard from "./TransportCard";
import LoadingMessage from "../LoadingMessage";
import {
  FaForward,
  FaBackward,
  FaPerson,
  FaChild,
  FaBaby,
} from "react-icons/fa6";
import { getTransportIcon } from "../../functions/ItineraryUtils";
import TransportSearchResults from "./TransportSearchResults";
import TransportCard from "./TransportCard";
import { getAgents } from "../../functions/Query";

interface Props {
  transportParams: TransportSearchParams;
  message: Message;
  openSearchResults: (
    type: string,
    response: SearchResponse[],
    params: TransportSearchParams
  ) => void;
}

const TransportSearchCard: React.FC<Props> = ({
  transportParams,
  message,
  openSearchResults,
}) => {
  const [searchResponse, setSearchResponse] = useState<SearchResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [sources, setSources] = useState<number[] | []>([]);
  const [completed, setCompleted] = useState(0);
  const [selectedTransportMode, setSelectedTransportMode] = useState<string | null>(null);

  const {
    mode,
    origin,
    destination,
    origin_coordinates,
    origin_country_code,
    destination_country_code,
    destination_coordinates,
    departure_date,
    return_date,
    adults = 1,
    children = 0,
    infants = 0,
  } = transportParams;

  

  const optionsDate: any = {
    weekday: "long",
    year: "numeric",
    month: "long",
    day: "numeric",
  };

  const display_departure_date = new Date(departure_date).toLocaleDateString(
    "fr-FR",
    optionsDate
  );

  const create_message_content = (
    message: Message,
    offers: TransportItem[]
  ) => {
    const text = [
      `${message.parsed.origin} ${message.parsed.destination} ${message.parsed.mode} search results:`,
    ];
    const results = offers.slice(0, 3).map((offer, i) => {
      return `${i + 1} - ${formatPrice(offer.A, offer.C)} - Dep. Time: ${
        offer.dep
      } - Arr. Time: ${offer.arr}`;
    });

    if (results.length > 0) {
      text.push(results.join("\n"));
    } else {
      text.push("- No offers found.");
    }

    return text.join("\n");
  };

  const fetchTransports = useCallback(async () => {
    setLoading(true);
    setError(null);
    setSearchResponse([]);
    setSelectedTransportMode(mode);

    try {
      if (
        origin_country_code &&
        destination_country_code
      ) {
        const source_agents: number[] = mode == "ferry" ? [9] : getAgents(
          origin_country_code,
          destination_country_code,
          mode
        );


        setSources(source_agents);

        source_agents.forEach(async (source: number) => {
          const res = await api.get(
            `${apiRoutes.multi_search_transports}?agent_id=${source}`,
            { params: transportParams }
          );

          const response: SearchResponse = res.data
            ? res.data
            : [];
          setSearchResponse((prev) => [...prev, response]);

          setCompleted((prev) => prev + 1);
        });
      } else {
        const response: SearchResponse = await api.get(apiRoutes.search_transports, {
          params: transportParams,
        });

        setSearchResponse((prev) => [...prev, response]);

      }

      // message.content = create_message_content(message, offers);
    } catch (err) {
      setError("Une erreur est survenue lors de la recherche de vols.");
    } finally {
      setLoading(false);
    }
  }, [transportParams]);

  useEffect(() => {
    fetchTransports();
  }, [fetchTransports]);

  // Ex: https://www.kayak.fr/flights/CDG-MAD/2024-07-15/2024-07-22
  const vivanodaUrl = `https://www.vivanoda.fr/results?ad=${adults}&sn=0&yg=0&ch=0&hl=fr&ori=${origin_coordinates}&dest=${destination_coordinates}&depDate=${departure_date}&retDate=${
    return_date ? return_date : ""
  }`;

  return (
    <Card className="my-4 border-0 bg-light rounded-4">
      <Card.Body>
        <Card.Title className="d-flex  justify-content-center align-items-center">
          {getTransportIcon(mode, 24, "me-2")}
          <div className="me-2">
            {mode.charAt(0).toUpperCase() + mode.slice(1)}
          </div>
          <strong>{`${origin} ${destination}`}</strong>
        </Card.Title>
        <Card.Text className="m-0 p-0">
          <div className="d-flex justify-content-evenly align-items-center">
            <div className="d-flex justify-content-between align-items-center border-info border-1 text-info rounded-2 p-2">
              <div>
                <FaForward className="me-1" />
                <strong>{formatDate(departure_date)}</strong>
              </div>

              {return_date && (
                <div className="ms-3">
                  <FaBackward className="me-1" />
                  <strong>{formatDate(return_date)}</strong>
                </div>
              )}
            </div>

            <div className="bg-info rounded-2 p-2 text-white d-flex align-items-center">
              <FaPerson size={18} />
              {adults === null && children === null && infants === null
                ? 1
                : adults === null
                ? 0
                : adults}
              {children > 0 && (
                <>
                  <FaChild size={16} /> {children}
                </>
              )}
              {infants > 0 && (
                <>
                  <FaBaby />
                  {infants}
                </>
              )}
            </div>
          </div>

          <br />
        </Card.Text>

        {loading && <LoadingMessage />}

        {/* {offers && (
          <Card.Text>
            {offers.slice(0, 10).map((offer) => (
              <OfferCard key={offer.id} offer={offer} />
            ))}
          </Card.Text>
        )} */}

        {!loading && searchResponse && (
          <TransportSearchResults
            responses={searchResponse}
            renderResult={(offer) => <TransportCard offer={offer} />}
            completed={(sources.length ? completed / sources.length : 1)}
            overview={true}
            results_length={3}
            transport_mode_str={selectedTransportMode}
          />
        )}

        <div className="d-flex justify-content-center">
          <Button
            variant="primary"
            onClick={() => openSearchResults(mode, searchResponse, transportParams)}
          >
            See all offers
          </Button>

          {/* <button className="btn btn-sm btn-outline-primary" onClick={fetchTransports}>
          🔄 Rechercher à nouveau
        </button> */}
        </div>
      </Card.Body>
    </Card>
  );
};

export default TransportSearchCard;
